import captureImg from 'shared/assets/images/discover/capture.png'
import careImg from 'shared/assets/images/discover/care.png'
import connectImg from 'shared/assets/images/discover/connect.png'
import convertImg from 'shared/assets/images/discover/convert.png'

import type { IFeatureFlagsStore } from 'entities/FeatureFlags'
import { SurveysPath } from 'entities/ExportRouters'
import {
  type IDiscoverPageData,
  type IDiscoverBoxData,
  type IDiscoverPagesKey,
  type IDiscoverBoxKey,
} from 'pages/discover/lib/types'
import { KeywordsPath } from 'pages/keywords/route/keywordsPath'
import { ChatbotPath } from 'pages/chatbot/route/chatbotPath'
import { BroadcastsPath } from 'pages/broadcasts/route/broadcastsPath'
import { CampaignsPath } from 'pages/campaigns/route/campaignsPath'
import { WorkflowsPath } from 'pages/workflows/route/workflowsPath'
import { PowerDialerPath } from 'pages/powerDialer/route/powerDialerPath'
import { ReferralsPath } from 'pages/settings/pages/referrals/route/referralsPath'
import { SettingsRoutes } from 'pages/settings'
import { AnalyticsRoutes } from 'pages/analytics'
import { ClickToTextPath } from 'pages/clickToText/route/clickToTextPath'
import { ReviewsPath } from 'pages/reviews'

export const DISCOVER_PAGES_DATA_MAP: Partial<Record<IDiscoverPagesKey, IDiscoverPageData>> = {
  capture: {
    title: 'Capture',
    image: captureImg,
    subtitle: 'Bring in more leads, automatically',
    description:
      'Use Salesmsg integrations and automation to capture leads from your CRM, forms, or website',
  },
  connect: {
    title: 'Connect',
    image: connectImg,
    subtitle: 'Start more conversations, faster',
    description: 'Reach leads instantly with texting and calling tools that drive engagement',
  },
  convert: {
    title: 'Convert',
    image: convertImg,
    subtitle: 'Turn conversations into customers',
    description:
      'Qualify, nurture, and follow up with your leads using powerful, automated workflows',
  },
  care: {
    title: 'Care',
    image: careImg,
    subtitle: 'Turn customers into advocates',
    description: 'Use messaging to follow up, request reviews, and deliver great customer support',
  },
}

export const DISCOVER_BOXES_DATA_MAP: Partial<Record<IDiscoverBoxKey, IDiscoverBoxData>> = {
  keywords: {
    id: 'keywords',
    title: 'Keywords',
    icon: 'keywords',
    description: 'Use keywords to capture leads',
    requiredPermission: 'can_manage_keywords',
    isActive: false,
    isNew: false,
    redirectTo: KeywordsPath.root,
  },
  websiteChatWidget: {
    id: 'websiteChatWidget',
    title: 'Website chat widget',
    icon: 'conversation',
    description: 'Extend the power of Salesmsg to your website and web app',
    requiredPermission: 'can_manage_website_chat_widget',
    isActive: false,
    isNew: false,
    redirectTo: `${SettingsRoutes.root}/${SettingsRoutes.websiteChatWidget}`,
  },
  missedCallAutomation: {
    id: 'missedCallAutomation',
    title: 'Missed call automation',
    icon: 'missedPhoneCall',
    description: 'Create targeted messaging campaigns to engage customers',
    requiredPermission: 'can_manage_inboxes',
    isActive: false,
    isNew: false,
    redirectTo: `${SettingsRoutes.root}/${SettingsRoutes.inboxes}`,
  },
  clickToText: {
    id: 'clickToText',
    title: 'Click to text',
    icon: 'messageTextCircle02',
    description: 'Bring a new and easy way to talk to customers',
    isActive: false,
    isNew: true,
    redirectTo: `${ClickToTextPath.root}`,
  },
  qrCode: {
    id: 'qrCode',
    title: 'QR code',
    icon: 'qr',
    description: 'Capture leads instantly with a quick scan',
    isActive: false,
    isNew: true,
    redirectTo: `${ClickToTextPath.root}`,
  },
  swipeUpToText: {
    id: 'swipeUpToText',
    title: 'Swipe up to text',
    icon: 'arrowUpCircle',
    description: 'Let Instagram users start a conversation with one swipe',
    isActive: false,
    isNew: true,
    redirectTo: `${ClickToTextPath.root}`,
  },
  ai: {
    id: 'ai',
    title: 'AI',
    icon: 'stars',
    description: 'Let AI handle conversations, book meetings and follow-ups for you',
    requiredPermission: 'can_manage_text_bots',
    isActive: false,
    isNew: false,
    redirectTo: `${ChatbotPath.root}`,
  },
  broadcasts: {
    id: 'broadcasts',
    title: 'Broadcasts',
    icon: 'broadcasts',
    description: 'Send mass messages to engage your entire contact base',
    requiredPermission: 'can_manage_broadcasts',
    isActive: false,
    isNew: false,
    redirectTo: BroadcastsPath.all,
  },
  campaigns: {
    id: 'campaigns',
    title: 'Campaigns',
    icon: 'campaigns',
    description: 'Create targeted messaging campaigns to engage customers',
    requiredPermission: 'can_manage_campaigns',
    isActive: false,
    isNew: false,
    redirectTo: CampaignsPath.all,
  },
  workflows: {
    id: 'workflows',
    title: 'Workflows',
    icon: 'workflows',
    description: 'Automate follow-ups and nurture leads with smart sequences',
    isActive: false,
    isNew: false,
    redirectTo: WorkflowsPath.root,
  },
  smartCallFlows: {
    id: 'smartCallFlows',
    title: 'Smart call flow',
    icon: 'smartCallFlows',
    description: 'Route incoming and missed calls with intelligent workflows',
    isActive: false,
    isNew: false,
    redirectTo: null,
  },
  ivr: {
    id: 'ivr',
    title: 'IVR',
    icon: 'dataflow',
    description: 'Guide callers with a smart voice menu',
    requiredPermission: 'can_manage_inboxes',
    isActive: false,
    isNew: false,
    redirectTo: `${SettingsRoutes.root}/${SettingsRoutes.inboxes}`,
  },
  powerDialer: {
    id: 'powerDialer',
    title: 'Power Dialer',
    icon: 'phoneRightArrows',
    description: 'Speed through calls with auto-dialing',
    isActive: false,
    isNew: false,
    redirectTo: PowerDialerPath.root,
  },
  conversionTracking: {
    id: 'conversionTracking',
    title: 'Conversion tracking',
    icon: 'solidBarChart',
    description: 'Track and measure your sales performance automatically',
    requiredPermission: 'can_access_analytics',
    isActive: false,
    isNew: false,
    redirectTo: `${AnalyticsRoutes.root}/${AnalyticsRoutes.conversions}`,
  },
  surveys: {
    id: 'surveys',
    title: 'Surveys',
    icon: 'checkList',
    description: 'Send targeted surveys to gather feedback and insights',
    isActive: false,
    isNew: true,
    redirectTo: SurveysPath.root,
  },
  reviews: {
    id: 'reviews',
    title: 'Reviews',
    icon: 'starFilled1',
    description: 'Collect customer reviews to build your reputation',
    isActive: false,
    isNew: false,
    redirectTo: null,
  },
  referrals: {
    id: 'referrals',
    title: 'Referrals',
    icon: 'dollar',
    description: 'Share Salesmsg with friends and earn rewards together',
    requiredPermission: 'can_manage_referrals',
    isActive: false,
    isNew: false,
    redirectTo: `${SettingsRoutes.root}/${ReferralsPath.root}`,
  },
}

export const getDiscoverBoxesDataMap = (
  featureFlagsStore: IFeatureFlagsStore
): Partial<Record<IDiscoverBoxKey, IDiscoverBoxData>> => {
  return {
    ...DISCOVER_BOXES_DATA_MAP,
    clickToText: {
      ...DISCOVER_BOXES_DATA_MAP.clickToText!,
      redirectTo: featureFlagsStore.canManageClickToText ? ClickToTextPath.root : null,
    },
    qrCode: {
      ...DISCOVER_BOXES_DATA_MAP.qrCode!,
      redirectTo: featureFlagsStore.canManageClickToText ? ClickToTextPath.root : null,
    },
    swipeUpToText: {
      ...DISCOVER_BOXES_DATA_MAP.swipeUpToText!,
      redirectTo: featureFlagsStore.canManageClickToText ? ClickToTextPath.root : null,
    },
    surveys: {
      ...DISCOVER_BOXES_DATA_MAP.surveys!,
      isNew: featureFlagsStore.surveys,
      redirectTo: featureFlagsStore.surveys ? SurveysPath.root : null,
    },
    reviews: {
      ...DISCOVER_BOXES_DATA_MAP.reviews!,
      isNew: featureFlagsStore.reviews,
      redirectTo: featureFlagsStore.reviews ? ReviewsPath.root : null,
    },
  }
}
